import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthContext'
import { apiFetch } from '../api/client'
import type { APIKey, APIKeyWithToken } from '../api/types'

interface APIKeyListResponse {
  api_keys: APIKey[]
}

export function useApiKeys(glowID: string) {
  const { idTokenProvider, user } = useAuth()
  return useQuery({
    queryKey: ['apiKeys', glowID],
    queryFn: async ({ signal }) => {
      const res = await apiFetch<APIKeyListResponse>(
        `/v1/glows/${glowID}/api-keys`,
        idTokenProvider,
        { signal },
      )
      return res.api_keys ?? []
    },
    enabled: !!user && !!glowID,
  })
}

// useCreateApiKey returns the plaintext token in the mutation result —
// callers must show it once (reveal-once modal) because the server
// never returns it again. Subsequent list reads only contain the hash.
export function useCreateApiKey(glowID: string) {
  const { idTokenProvider } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiFetch<APIKeyWithToken>(`/v1/glows/${glowID}/api-keys`, idTokenProvider, {
        method: 'POST',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['apiKeys', glowID] })
    },
  })
}

export function useRevokeApiKey(glowID: string) {
  const { idTokenProvider } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (keyHash: string) =>
      apiFetch<void>(
        `/v1/glows/${glowID}/api-keys/${encodeURIComponent(keyHash)}`,
        idTokenProvider,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['apiKeys', glowID] })
    },
  })
}
